// src/components/OpenAPISchemaManagement.js

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import yaml from 'js-yaml';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Editor from '@monaco-editor/react';
import {
  FaBolt,
  FaCheckCircle,
  FaCode,
  FaExclamationTriangle,
  FaFileCode,
  FaKey,
  FaLayerGroup,
  FaPen,
  FaPlay,
  FaPlus,
  FaRobot,
  FaShieldAlt,
  FaSpinner,
  FaTrash,
} from 'react-icons/fa';
import './OpenAPISchemaManagement.css';

const editorOptions = {
  minimap: { enabled: false },
  fontSize: 13,
  lineNumbers: 'on',
  scrollBeyondLastLine: false,
  automaticLayout: true,
  fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
  padding: { top: 16, bottom: 16 },
  smoothScrolling: true,
  renderLineHighlight: 'all',
  overviewRulerBorder: false,
  overviewRulerLanes: 0,
  hideCursorInOverviewRuler: true,
  scrollbar: {
    vertical: 'visible',
    horizontal: 'visible',
    useShadows: false,
    verticalScrollbarSize: 10,
    horizontalScrollbarSize: 10,
  },
};

const OpenAPISchemaManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState(undefined);
  const [schemas, setSchemas] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [schemaContent, setSchemaContent] = useState('');
  const [operations, setOperations] = useState([]);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [parameters, setParameters] = useState({});
  const [response, setResponse] = useState(null);
  const [testError, setTestError] = useState(null);
  const [apiKeys, setApiKeys] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [invoking, setInvoking] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const parsedSchema = useMemo(() => {
    if (!schemaContent.trim()) return null;
    try {
      return yaml.load(schemaContent);
    } catch (parseError) {
      return null;
    }
  }, [schemaContent]);

  const securitySchemes = useMemo(() => {
    const schemes = parsedSchema?.components?.securitySchemes || {};
    return Object.entries(schemes).map(([name, config]) => ({
      name,
      type: config?.type,
      scheme: config?.scheme,
      in: config?.in,
    }));
  }, [parsedSchema]);

  const schemaStats = useMemo(() => ({
    total: schemas.length,
    operations: operations.length,
    secured: securitySchemes.length,
  }), [operations.length, schemas.length, securitySchemes.length]);

  const fetchUserData = useCallback(async (token) => {
    try {
      const response = await axios.get('/api/users/user', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
    } catch (fetchError) {
      console.error('Failed to fetch user data:', fetchError);
      setError(t('openAPISchemaManagement.fetchUserError', 'Unable to load your user profile.'));
      setUser(null);
    }
  }, [t]);

  const fetchSchemas = useCallback(async (token) => {
    try {
      const response = await axios.get('/api/ai-assistants/openapi-schemas', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSchemas(response.data);
    } catch (fetchError) {
      console.error('Failed to fetch schemas:', fetchError);
      setError(t('openAPISchemaManagement.fetchSchemasError', 'Unable to load OpenAPI tools.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchOperations = useCallback(async (schemaId, token) => {
    try {
      const response = await axios.get(
        `/api/ai-assistants/openapi-schemas/${schemaId}/operations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOperations(response.data);
    } catch (fetchError) {
      console.error('Failed to fetch operations:', fetchError);
      setOperations([]);
      setError(t('openAPISchemaManagement.fetchOperationsError', 'Unable to inspect operations for this tool.'));
    }
  }, [t]);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/');
      return;
    }

    fetchUserData(token);
  }, [fetchUserData, navigate]);

  useEffect(() => {
    if (user) {
      const token = localStorage.getItem('access_token');
      fetchSchemas(token);
    }
  }, [fetchSchemas, user]);

  useEffect(() => {
    setApiKeys((currentKeys) => {
      const nextKeys = {};
      securitySchemes.forEach((scheme) => {
        if (scheme.type === 'http' && scheme.scheme === 'basic') {
          nextKeys[`${scheme.name}_username`] = currentKeys[`${scheme.name}_username`] || '';
          nextKeys[`${scheme.name}_password`] = currentKeys[`${scheme.name}_password`] || '';
        } else {
          nextKeys[scheme.name] = currentKeys[scheme.name] || '';
        }
      });
      return nextKeys;
    });
  }, [securitySchemes]);

  const resetForm = useCallback(() => {
    setSelectedSchema(null);
    setTitle('');
    setDescription('');
    setSchemaContent('');
    setOperations([]);
    setSelectedOperation(null);
    setParameters({});
    setResponse(null);
    setTestError(null);
    setError('');
    setSuccessMessage('');
  }, []);

  const handleSelectSchema = async (schema) => {
    if (selectedSchema?._id === schema._id) {
      resetForm();
      return;
    }

    setSelectedSchema(schema);
    setTitle(schema.title || '');
    setDescription(schema.description || '');
    setSchemaContent(schema.schema_content || '');
    setApiKeys(schema.api_keys || {});
    setResponse(null);
    setTestError(null);
    setSelectedOperation(null);
    setParameters({});

    const token = localStorage.getItem('access_token');
    await fetchOperations(schema._id, token);
  };

  const handleSelectOperation = (operationId) => {
    const operation = operations.find((op) => op.operationId === operationId) || null;
    setSelectedOperation(operation);
    setResponse(null);
    setTestError(null);

    if (!operation) {
      setParameters({});
      return;
    }

    const initialParams = {};
    (operation.parameters || []).forEach((param) => {
      initialParams[param.name] = '';
    });
    setParameters(initialParams);
  };

  const handleParameterChange = (paramName, value) => {
    setParameters((currentParameters) => ({
      ...currentParameters,
      [paramName]: value,
    }));
  };

  const handleSaveSchema = async () => {
    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('access_token');
      const payload = {
        title,
        description,
        schema_content: schemaContent,
        api_keys: apiKeys,
      };

      if (selectedSchema) {
        await axios.put(
          `/api/ai-assistants/openapi-schemas/${selectedSchema._id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        resetForm();
        setSuccessMessage(t('openAPISchemaManagement.updateSuccess', 'Tool updated successfully.'));
      } else {
        await axios.post('/api/ai-assistants/openapi-schemas', payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        resetForm();
        setSuccessMessage(t('openAPISchemaManagement.createSuccess', 'Tool created successfully.'));
      }

      await fetchSchemas(token);
    } catch (saveError) {
      console.error('Failed to save schema:', saveError);
      setError(
        selectedSchema
          ? t('openAPISchemaManagement.updateError', 'Unable to update this tool.')
          : t('openAPISchemaManagement.createError', 'Unable to create this tool.')
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSchema = async (schemaId) => {
    const confirmDelete = window.confirm(
      t('openAPISchemaManagement.confirmDelete', 'Delete this OpenAPI tool?')
    );
    if (!confirmDelete) return;

    try {
      setDeletingId(schemaId);
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/ai-assistants/openapi-schemas/${schemaId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchSchemas(token);
      if (selectedSchema?._id === schemaId) resetForm();
      setSuccessMessage(t('openAPISchemaManagement.deleteSuccess', 'Tool deleted successfully.'));
    } catch (deleteError) {
      console.error('Failed to delete schema:', deleteError);
      setError(t('openAPISchemaManagement.deleteError', 'Unable to delete this tool.'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleInvoke = async () => {
    if (!selectedOperation) return;

    const token = localStorage.getItem('access_token');
    setInvoking(true);
    setResponse(null);
    setTestError(null);

    try {
      const invokeResponse = await axios.post(
        '/api/ai-assistants/openapi-schemas/invoke',
        {
          operation_id: selectedOperation.operationId,
          parameters,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResponse(invokeResponse.data);
    } catch (invokeError) {
      console.error('API invocation failed:', invokeError);
      setTestError(invokeError.response ? invokeError.response.data.detail : 'Unknown error');
    } finally {
      setInvoking(false);
    }
  };

  const getMethodClass = (method) => `method-${(method || 'get').toLowerCase()}`;
  const isSaveDisabled = saving || !title.trim() || !schemaContent.trim();

  return (
    <div className="openapi-workspace">
      <header className="openapi-hero">
        <div className="openapi-hero-title">
          <span className="openapi-hero-icon">
            <FaRobot />
          </span>
          <div>
            <p className="openapi-eyebrow">{t('codeSnippets.tabTitle', 'Custom Tools')}</p>
            <h1>{t('openAPISchemaManagement.title', 'OpenAPI Tool Builder')}</h1>
            <p>
              {t(
                'openAPISchemaManagement.subtitle',
                'Create secure OpenAPI tools your AI agents can call during conversations.'
              )}
            </p>
          </div>
        </div>

      </header>

      <section className="openapi-stats-grid" aria-label="OpenAPI tool summary">
        <div className="openapi-stat-card">
          <FaLayerGroup />
          <div>
            <strong>{schemaStats.total}</strong>
            <span>{t('openAPISchemaManagement.yourSchemas', 'Saved tools')}</span>
          </div>
        </div>
        <div className="openapi-stat-card">
          <FaBolt />
          <div>
            <strong>{schemaStats.operations}</strong>
            <span>{t('openAPISchemaManagement.operations', 'Operations')}</span>
          </div>
        </div>
        <div className="openapi-stat-card">
          <FaShieldAlt />
          <div>
            <strong>{schemaStats.secured}</strong>
            <span>{t('openAPISchemaManagement.apiKeysLabel', 'Security schemes')}</span>
          </div>
        </div>
      </section>

      {(error || successMessage) && (
        <div className={`openapi-feedback ${error ? 'error' : 'success'}`}>
          {error ? <FaExclamationTriangle /> : <FaCheckCircle />}
          <span>{error || successMessage}</span>
        </div>
      )}

      <main className="openapi-layout">
        <aside className="openapi-sidebar">
          <div className="openapi-panel-heading">
            <div>
              <h2>{t('openAPISchemaManagement.yourSchemas', 'Saved tools')}</h2>
              <p>{t('openAPISchemaManagement.savedToolsHint', 'Select a tool to edit or test it.')}</p>
            </div>
          </div>

          {loading ? (
            <div className="openapi-loading">
              <FaSpinner className="spinner" />
              <span>{t('common.loading', 'Loading')}</span>
            </div>
          ) : (
            <div className="openapi-tool-list">
              {selectedSchema && (
                <button
                  type="button"
                  className="openapi-tool-item create"
                  onClick={resetForm}
                >
                  <span className="openapi-tool-icon"><FaPlus /></span>
                  <span>
                    <strong>{t('openAPISchemaManagement.addSchema', 'New tool')}</strong>
                    <small>{t('openAPISchemaManagement.startFromSpec', 'Start from an OpenAPI spec')}</small>
                  </span>
                </button>
              )}

              {schemas.length === 0 && (
                <div className="openapi-sidebar-empty">
                  <FaFileCode />
                  <strong>{t('openAPISchemaManagement.noSchemas', 'No saved tools yet')}</strong>
                  <span>{t('openAPISchemaManagement.noSchemasHint', 'Fill the builder form to create the first one.')}</span>
                </div>
              )}

              {schemas.map((schema) => (
                <div
                  key={schema._id}
                  className={`openapi-tool-card ${selectedSchema?._id === schema._id ? 'active' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelectSchema(schema)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleSelectSchema(schema);
                    }
                  }}
                >
                  <div className="openapi-tool-card-main">
                    <span className="openapi-tool-icon"><FaFileCode /></span>
                    <div>
                      <strong>{schema.title}</strong>
                      <p>{schema.description || t('openAPISchemaManagement.noDescription', 'No description')}</p>
                    </div>
                  </div>
                  <div className="openapi-tool-card-footer">
                    <span><FaBolt /> {schema.operations?.length || 0}</span>
                    <button
                      type="button"
                      className="openapi-delete-button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteSchema(schema._id);
                      }}
                      aria-label={t('openAPISchemaManagement.deleteAriaLabel', { title: schema.title })}
                      disabled={deletingId === schema._id}
                    >
                      {deletingId === schema._id ? <FaSpinner className="spinner" /> : <FaTrash />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </aside>

        <section className="openapi-builder-panel">
          <div className="openapi-panel-heading">
            <div>
              <h2>
                {selectedSchema
                  ? t('openAPISchemaManagement.editSchema', 'Edit tool')
                  : t('openAPISchemaManagement.createSchema', 'Create tool')}
              </h2>
              <p>
                {t(
                  'openAPISchemaManagement.builderHint',
                  'Name the tool, paste the OpenAPI schema, then save it for your AI agents.'
                )}
              </p>
            </div>
            <span className="openapi-mode-pill">
              {selectedSchema ? <FaPen /> : <FaPlus />}
              {selectedSchema ? t('common.edit', 'Edit') : t('common.new', 'New')}
            </span>
          </div>

          <div className="openapi-form-grid">
            <label className="openapi-field">
              <span>{t('openAPISchemaManagement.titleLabel', 'Tool name')}</span>
              <input
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder={t('openAPISchemaManagement.titlePlaceholder', 'e.g. Shopify order lookup')}
              />
            </label>

            <label className="openapi-field">
              <span>{t('openAPISchemaManagement.descriptionLabel', 'Agent description')}</span>
              <textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder={t(
                  'openAPISchemaManagement.descriptionPlaceholder',
                  'Explain when the AI agent should use this tool.'
                )}
              />
            </label>
          </div>

          <div className="openapi-editor-shell">
            <div className="openapi-editor-header">
              <div>
                <h3><FaCode /> {t('openAPISchemaManagement.schemaLabel', 'OpenAPI schema')}</h3>
                <p>{t('openAPISchemaManagement.schemaHint', 'YAML or JSON OpenAPI 3 schema.')}</p>
              </div>
              <span className={`openapi-parse-pill ${parsedSchema ? 'valid' : 'empty'}`}>
                {parsedSchema
                  ? t('openAPISchemaManagement.schemaParsed', 'Parsed')
                  : t('openAPISchemaManagement.waitingSchema', 'Waiting for schema')}
              </span>
            </div>
            <Editor
              height="460px"
              language="yaml"
              theme="vs-dark"
              value={schemaContent}
              onChange={(value) => setSchemaContent(value || '')}
              options={editorOptions}
            />
          </div>

          {securitySchemes.length > 0 && (
            <div className="openapi-security-panel">
              <div className="openapi-panel-heading compact">
                <div>
                  <h3><FaKey /> {t('openAPISchemaManagement.apiKeysLabel', 'Credentials')}</h3>
                  <p>{t('openAPISchemaManagement.credentialsHint', 'Stored with this tool and used during invocation.')}</p>
                </div>
              </div>

              <div className="openapi-security-grid">
                {securitySchemes.map((scheme) => (
                  <div key={scheme.name} className="openapi-security-card">
                    <div className="openapi-security-card-header">
                      <strong>{scheme.name}</strong>
                      <span>{scheme.type}{scheme.scheme ? ` / ${scheme.scheme}` : ''}</span>
                    </div>
                    {scheme.type === 'http' && scheme.scheme === 'basic' ? (
                      <>
                        <input
                          type="text"
                          placeholder={t('openAPISchemaManagement.usernamePlaceholder', 'Username')}
                          value={apiKeys[`${scheme.name}_username`] || ''}
                          onChange={(event) =>
                            setApiKeys({ ...apiKeys, [`${scheme.name}_username`]: event.target.value })
                          }
                        />
                        <input
                          type="password"
                          placeholder={t('openAPISchemaManagement.passwordPlaceholder', 'Password')}
                          value={apiKeys[`${scheme.name}_password`] || ''}
                          onChange={(event) =>
                            setApiKeys({ ...apiKeys, [`${scheme.name}_password`]: event.target.value })
                          }
                        />
                      </>
                    ) : (
                      <input
                        type="password"
                        placeholder={t('openAPISchemaManagement.apiKeyPlaceholder', 'API key or token')}
                        value={apiKeys[scheme.name] || ''}
                        onChange={(event) => setApiKeys({ ...apiKeys, [scheme.name]: event.target.value })}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="openapi-save-bar">
            <button className="openapi-secondary-action" type="button" onClick={resetForm}>
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              className="openapi-primary-action"
              type="button"
              onClick={handleSaveSchema}
              disabled={isSaveDisabled}
            >
              {saving ? <FaSpinner className="spinner" /> : selectedSchema ? <FaPen /> : <FaPlus />}
              {selectedSchema
                ? t('openAPISchemaManagement.updateSchema', 'Update tool')
                : t('openAPISchemaManagement.createSchema', 'Create tool')}
            </button>
          </div>
        </section>

        <aside className="openapi-test-panel">
          <div className="openapi-panel-heading">
            <div>
              <h2>{t('openAPISchemaManagement.testingTitle', 'Test operation')}</h2>
              <p>{t('openAPISchemaManagement.testingHint', 'Run an operation before assigning it to an agent.')}</p>
            </div>
          </div>

          {!selectedSchema ? (
            <div className="openapi-empty-state">
              <FaPlay />
              <h3>{t('openAPISchemaManagement.selectToolFirst', 'Select a saved tool')}</h3>
              <p>{t('openAPISchemaManagement.selectToolHint', 'Operations are available after the schema is saved.')}</p>
            </div>
          ) : operations.length === 0 ? (
            <div className="openapi-empty-state">
              <FaBolt />
              <h3>{t('openAPISchemaManagement.noOperations', 'No operations found')}</h3>
              <p>{t('openAPISchemaManagement.noOperationsHint', 'Save a valid OpenAPI schema with operationId values.')}</p>
            </div>
          ) : (
            <>
              <label className="openapi-field">
                <span>{t('openAPISchemaManagement.selectOperation', 'Operation')}</span>
                <select
                  value={selectedOperation?.operationId || ''}
                  onChange={(event) => handleSelectOperation(event.target.value)}
                >
                  <option value="">{t('openAPISchemaManagement.selectOperationPlaceholder', 'Choose an operation')}</option>
                  {operations.map((operation) => (
                    <option key={operation.operationId} value={operation.operationId}>
                      {operation.operationId}
                    </option>
                  ))}
                </select>
              </label>

              {selectedOperation && (
                <div className="openapi-operation-detail">
                  <div className="openapi-operation-route">
                    <span className={`openapi-method ${getMethodClass(selectedOperation.method)}`}>
                      {selectedOperation.method?.toUpperCase()}
                    </span>
                    <code>{selectedOperation.path}</code>
                  </div>

                  <div className="openapi-parameters">
                    <h3>{t('openAPISchemaManagement.parametersTitle', 'Parameters')}</h3>
                    {(selectedOperation.parameters || []).length > 0 ? (
                      selectedOperation.parameters.map((param) => (
                        <label className="openapi-field" key={param.name}>
                          <span>
                            {param.name}
                            {param.required ? <em>{t('common.required', 'Required')}</em> : null}
                          </span>
                          <input
                            type="text"
                            value={parameters[param.name] || ''}
                            onChange={(event) => handleParameterChange(param.name, event.target.value)}
                            placeholder={`${param.in || 'param'} value`}
                          />
                        </label>
                      ))
                    ) : (
                      <p className="openapi-muted">{t('openAPISchemaManagement.noParameters', 'No parameters required.')}</p>
                    )}
                  </div>

                  <button className="openapi-primary-action full" type="button" onClick={handleInvoke} disabled={invoking}>
                    {invoking ? <FaSpinner className="spinner" /> : <FaPlay />}
                    {t('openAPISchemaManagement.invokeOperation', 'Run operation')}
                  </button>
                </div>
              )}

              {testError && (
                <div className="openapi-result error">
                  <h3><FaExclamationTriangle /> {t('openAPISchemaManagement.testError', 'Test failed')}</h3>
                  <pre>{typeof testError === 'string' ? testError : JSON.stringify(testError, null, 2)}</pre>
                </div>
              )}

              {response && (
                <div className="openapi-result success">
                  <h3><FaCheckCircle /> {t('openAPISchemaManagement.responseTitle', 'Response')}</h3>
                  <pre>{JSON.stringify(response, null, 2)}</pre>
                </div>
              )}
            </>
          )}
        </aside>
      </main>
    </div>
  );
};

export default OpenAPISchemaManagement;
